import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { Clock, MapPin } from 'lucide-react'
import { Logo } from '@/components/generals/logo'
import { ButtonLink } from '@/components/generals/button'
import { FOOTER_NAV } from '@/constants/navigation'
import { siteConfig } from '@/constants/site'

export const SiteFooter: FC = (): ReactNode => {
    return (
        <footer className='relative overflow-hidden bg-maroon-deep text-ivory'>
            <div aria-hidden='true' className='pattern-jaali absolute inset-0 text-gold-light/[0.05]' />

            <div className='relative mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:py-20'>
                <div className='grid gap-12 sm:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1.3fr]'>
                    <div>
                        <Logo tone='light' />

                        <p className='mt-6 max-w-xs text-sm leading-relaxed text-ivory/70'>
                            Planning and design for Indian weddings, from the first roka to the final vidaai, across India and abroad.
                        </p>
                    </div>

                    {FOOTER_NAV.map(group => (
                        <nav key={group.title} aria-label={group.title}>
                            <h2 className='font-sans text-xs font-medium uppercase tracking-[0.28em] text-gold-light'>
                                {group.title}
                            </h2>

                            <ul className='mt-5 space-y-3'>
                                {group.links.map(link => (
                                    <li key={link.href}>
                                        <Link href={link.href} className='text-sm text-ivory/80 transition-colors hover:text-ivory focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'>
                                            {link.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    ))}

                    <div>
                        <h2 className='font-sans text-xs font-medium uppercase tracking-[0.28em] text-gold-light'>
                            The studio
                        </h2>

                        <ul className='mt-5 space-y-3 text-sm text-ivory/80'>
                            <li className='flex gap-3'>
                                <MapPin aria-hidden='true' className='mt-0.5 size-4 shrink-0 text-gold-light' />
                                {siteConfig.studio}
                            </li>

                            <li className='flex gap-3'>
                                <Clock aria-hidden='true' className='mt-0.5 size-4 shrink-0 text-gold-light' />
                                {siteConfig.hours}
                            </li>
                        </ul>

                        <ButtonLink href='/contact' variant='secondary' size='sm' className='mt-7'>
                            Book a consultation
                        </ButtonLink>
                    </div>
                </div>

                <div className='mt-14 flex flex-col gap-3 border-t border-ivory/15 pt-8 text-xs text-ivory/60 sm:flex-row sm:items-center sm:justify-between'>
                    <p>
                        © <span suppressHydrationWarning>{new Date().getFullYear()}</span> {siteConfig.name}. All rights reserved.
                    </p>

                    <p>
                        Planning Indian weddings since 2014.
                    </p>
                </div>
            </div>
        </footer>
    )
}
